import RefreshIcon from "@assets/icons/refresh-icon.svg";
import EmailSyncIcon from "@assets/icons/email-sync-icon.svg";

import { cn } from "@utils/tailwind.util";
import { SyncEmailScreenAction } from "@enums/screens.enum";
import { ImageSection } from "@components/Sections/ImageSection";
import { Paragraph } from "@ui/Content";
import { Button } from "@ui/Button";
import { View } from "@ui/View";
import { SectionHeading } from "@ui/Headings";

interface Props {
  isPending: boolean;
  onSave: VoidFunction;
  onSubscribe: VoidFunction;
  currentAction: SyncEmailScreenAction;
}

export default function SyncFirstEmailScreen({
  isPending,
  onSave,
  onSubscribe,
  currentAction,
}: Props) {
  const primaryButtonClasses = isPending
    ? "bg-teal-100 text-white dark:bg-gray-700 dark:text-teal-100"
    : "bg-teal-500 text-white";
    
  const secondaryButtonClasses = isPending
    ? "bg-gray-50 dark:bg-gray-700 dark:text-teal-100"
    : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80";
  
  return (
    <>
      <ImageSection
        className={"mt-3 mb-3"}
        mainIcon={<EmailSyncIcon className="max-h-16 max-w-16 z-20 object-contain" />}
        overlayIcon={
          isPending ? (
            <RefreshIcon className="stroke-gray-550 stroke-[1.76] animate-slowpin-reverse" />
          ) : null
        }
        overlayIconBackground={"bg-white"}
      />

      <div className="flex flex-col gap-4 pt-2 pb-6 px-4">
        <Paragraph>Sync to add notes and tags</Paragraph>
      </div>

      <div className="flex flex-col items-center gap-[18px]">
        <View.Condition if={currentAction !== SyncEmailScreenAction.Subscribe}>
          <Button
            className={cn(
              "min-w-[170px] h-8",
              primaryButtonClasses
            )}
            buttonType={isPending ? "TRANSPARENT" : "PRIMARY"}
            onClick={onSave}
            disabled={isPending}
          >
            {isPending ? "Syncing Email" : "Sync Email"}
          </Button>
        </View.Condition>

        <View.Condition if={!currentAction}>
          <span className="text-gray-950 dark:text-white text-sm font-[400] leading-5">or</span>
        </View.Condition>

        <View.Condition if={currentAction !== SyncEmailScreenAction.Sync}>
          <Button
            className={cn(
              "min-w-[170px] h-8",
              currentAction ? primaryButtonClasses : secondaryButtonClasses
            )}
            buttonType={isPending || !currentAction ? "TRANSPARENT" : "PRIMARY"}
            onClick={onSubscribe}
            disabled={isPending}
          >
            {isPending ? "Subscribing Thread" : "Subscribe Thread"}
          </Button>
        </View.Condition>
      </div>

      <div className="flex flex-col w-full mt-10 opacity-50">
        <SectionHeading>Tags</SectionHeading>
        {!isPending && <span className="text-sm text-gray-500 mt-3">No tags yet</span>}
      </div>

      {isPending && (
        <div className="relative w-full mt-2 mb-4 overflow-hidden rounded bg-gray-200 h-[5px]">
          <div className="absolute h-full w-1/3 bg-gradient-to-r from-transparent via-teal-500/70 to-transparent animate-bounce-bar" />
        </div>
      )}
    </>
  );
}
